import { ApiBase } from "@/api/ApiBase";
import { Formatter } from "@/utils/Formatter";

export interface ArtigoResumo {
  id: number;
  nome: string;
  codigoArtigo: string | null;
  codigo: string;
  codigoBarras: string | null;
  precoCompra: number;
  foto: string | null;
  precoVenda: number;
  retencao: string;
  nomeCategoria: string;
  codigoImposto: string;
  unidadeMedida: string;
  taxa: number;
  siglaTaxa: string;
  nomeTaxa: string;
}

export interface ListaArtigosResponse {
  itens: ArtigoResumo[];
  total: number;
}

export interface ArtigoDetalhe {
  id: number;
  nome: string;
  tipo: "P" | "S";
  foto: string | null;
  retencao: string;
  precoCompra: number;
  precoVenda: number;
  codigo: string;
  codigoArtigo: string | null;
  codigoBarras: string | null;
  unidadeMedida: string;
  descricao: string | null;
  stockMin: number;
  stockMax: number;
  canal: string;
  dataCriacao: string;
  criador: { nome: string } | null;
  categoria: { id: number; nome: string } | null;
  fornecedor: { id: number; nome: string } | null;
  imposto: {
    id: number;
    taxa: number;
    nome: string;
    sigla: string;
    codigo: string;
  } | null;
  grupo: { id: number; nome: string; codigo: string } | null;
  isencao: { id: number; motivo: string; codigo: string } | null;
}

export interface EditarApagarArtigoResponse {
  msg: string;
  tipo: "sucesso" | "erro";
}

export interface PrecoPorEstabelecimento {
  id: number;
  precoCompra: number;
  precoVenda: number;
  estabelecimentoId: number;
  nomeEstabelecimento: string;
  numeroEstabelecimento: string;
  dataCriacao: string;
}

export interface RespostaPrecoPorEstabelecimento {
  msg: string;
  tipo: "sucesso" | "erro";
  precos: PrecoPorEstabelecimento[];
}

export interface DadosArtigoForm {
  nome: string;
  tipo: "P" | "S";
  categoriaId: number;
  fornecedorId: number;
  impostoId: number;
  isencaoId: number | null;
  grupoId: number | null;
  codigoArtigo: string;
  codigoBarras: string;
  descricao: string;
  unidadeMedida: string;
  stockMin: number;
  stockMax: number;
  retencao: string;
  precoCompra: number;
  precoVenda: number;
  foto: File | null;
}

export const ARTIGOS_POR_PAGINA = 100;

function formatarDinheiroParaBackend(valor: number): string {
  return Formatter.moeda(valor);
}

export class ArtigosApi extends ApiBase {
  listar(tipo: "P" | "S", pagina: number): Promise<ListaArtigosResponse> {
    const chaveItens = tipo === "P" ? "produtos" : "serviços";
    const chaveTotal = tipo === "P" ? "totalProdutos" : "totalServiços";
    return this.http
      .put<Record<string, unknown>>(
        `/api/facturacao/artigo/lista-by-comerciante/${pagina}/${tipo}`,
      )
      .then((resposta) => ({
        itens: (resposta[chaveItens] as ArtigoResumo[]) ?? [],
        total: (resposta[chaveTotal] as number) ?? 0,
      }));
  }

  obterPorCodigo(codigo: string): Promise<ArtigoDetalhe> {
    return this.http.put<ArtigoDetalhe>(
      `/api/facturacao/artigo/get-one-by-codigo/${encodeURIComponent(codigo)}`,
    );
  }

  private construirFormData(
    dados: DadosArtigoForm,
    codigoExistente?: string,
  ): FormData {
    const fd = new FormData();
    if (codigoExistente) fd.append("codigo", codigoExistente);
    fd.append("nome", dados.nome);
    fd.append("tipo", dados.tipo);
    fd.append("categoria.id", String(dados.categoriaId));
    fd.append("fornecedor.id", String(dados.fornecedorId));
    fd.append("imposto.id", String(dados.impostoId));
    fd.append(
      "isencao.id",
      dados.isencaoId != null ? String(dados.isencaoId) : "",
    );
    if (dados.grupoId != null) {
      fd.append("grupo.id", String(dados.grupoId));
    }
    fd.append("codigoArtigo", dados.codigoArtigo.trim());
    fd.append("codigoBarras", dados.codigoBarras.trim());
    fd.append("descricao", dados.descricao);
    fd.append("unidadeMedida", dados.unidadeMedida);
    fd.append("stockMin", String(dados.tipo === "P" ? dados.stockMin : 0));
    fd.append("stockMax", String(dados.tipo === "P" ? dados.stockMax : 0));
    if (dados.tipo === "S") fd.append("retencao", dados.retencao);
    fd.append("txtValorCompra", formatarDinheiroParaBackend(dados.precoCompra));
    fd.append("txtValorVenda", formatarDinheiroParaBackend(dados.precoVenda));
    if (dados.foto) fd.append("fotoArtigo", dados.foto);
    return fd;
  }

  adicionar(dados: DadosArtigoForm): Promise<EditarApagarArtigoResponse> {
    return this.http.postMultipart<EditarApagarArtigoResponse>(
      "/api/facturacao/artigo/add",
      this.construirFormData(dados),
    );
  }

  editar(
    codigo: string,
    dados: DadosArtigoForm,
  ): Promise<EditarApagarArtigoResponse> {
    return this.http.postMultipart<EditarApagarArtigoResponse>(
      "/api/facturacao/artigo/edit",
      this.construirFormData(dados, codigo),
    );
  }

  apagar(codigo: string): Promise<EditarApagarArtigoResponse> {
    return this.http.post<EditarApagarArtigoResponse>(
      "/api/facturacao/artigo/delete",
      { codigo },
    );
  }

  listarPrecosPorEstabelecimento(
    codigo: string,
  ): Promise<PrecoPorEstabelecimento[]> {
    return this.http.put<PrecoPorEstabelecimento[]>(
      `/api/facturacao/artigo/preco-estabelecimento/lista/${encodeURIComponent(codigo)}`,
    );
  }

  adicionarPrecoPorEstabelecimento(dados: {
    codigoArtigo: string;
    estabelecimentoId: number;
    precoCompra: number;
    precoVenda: number;
  }): Promise<RespostaPrecoPorEstabelecimento> {
    return this.http.post<RespostaPrecoPorEstabelecimento>(
      "/api/facturacao/artigo/preco-estabelecimento/add",
      {
        codigoArtigo: dados.codigoArtigo,
        estabelecimentoId: String(dados.estabelecimentoId),
        txtValorCompra: formatarDinheiroParaBackend(dados.precoCompra),
        txtValorVenda: formatarDinheiroParaBackend(dados.precoVenda),
      },
    );
  }

  removerPrecoPorEstabelecimento(
    codigoArtigo: string,
    estabelecimentoId: number,
  ): Promise<RespostaPrecoPorEstabelecimento> {
    return this.http.post<RespostaPrecoPorEstabelecimento>(
      "/api/facturacao/artigo/preco-estabelecimento/delete",
      { codigoArtigo, estabelecimentoId: String(estabelecimentoId) },
    );
  }
}
